import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  output,
  inject,
  input,
} from '@angular/core';
import { AppointmentTypesService } from '@fitness-force/api';
import { NotFoundError } from '@fitness-force/models';
import { ErrorMsgComponent } from '@fitness-force/errors';

import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { COMPANY_INFO, TENANTS_INFO } from '@fitness-force/state';
import { ToastMessageService } from '@fitness-force/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { combineLatest, Observable } from 'rxjs';

import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  Validators,
  UntypedFormGroup,
  AbstractControl,
} from '@angular/forms';
import { SelectModule } from 'primeng/select';

@Component({
  selector: 'tag-create',
  templateUrl: './tag-create.component.html',

  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    RippleModule,
    DialogModule,
    DividerModule,
    InputTextModule,
    TranslateModule,
    ErrorMsgComponent,
    SelectModule,
  ],
})
export class TagCreateComponent implements OnInit {
  private store = inject(Store);
  private appointmentTypesService = inject(AppointmentTypesService);
  private toastMessageService = inject(ToastMessageService);
  private translate = inject(TranslateService);
  private fb = inject(UntypedFormBuilder);
  private changeDetectorRef = inject(ChangeDetectorRef);

  readonly activeTag = input<any>();
  localActiveTag: any = null;
  readonly changed = output();
  readonly close = output();
  getAccessTenantList$: Observable<any> = new Observable();
  showOptions: boolean = false;
  tenantId: number = 0;

  tagForm: UntypedFormGroup;
  primaryLanguage!: string;
  secondaryLanguage!: string;
  isSecondaryLanguage = false;
  apptCatParams = new Map();

  ngOnInit(): void {
    this.getAccessTenantList$ = this.store.select(TENANTS_INFO);

    combineLatest([
      this.getAccessTenantList$,
      this.store.select(COMPANY_INFO),
    ]).subscribe({
      next: ([tenant, company]: any) => {
        this.tenantId = tenant.selected_tenant.id;
        if (this.tenantId > 0) {
          this.store.dispatch({ type: 'ROUTER_GO' });

          if (tenant.selected_tenant?.secondary_language) {
            this.isSecondaryLanguage = true;
            this.translate
              .get([
                'common.LANGUAGE.' +
                  tenant.selected_tenant.primary_language?.toUpperCase(),
                'common.LANGUAGE.' +
                  tenant.selected_tenant.secondary_language?.toUpperCase(),
              ])
              .subscribe((translations) => {
                this.primaryLanguage =
                  translations[
                    'common.LANGUAGE.' +
                      tenant.selected_tenant.primary_language?.toUpperCase()
                  ];
                this.secondaryLanguage =
                  translations[
                    'common.LANGUAGE.' +
                      tenant.selected_tenant.secondary_language?.toUpperCase()
                  ];
              });
          }
          this.loadCategory();
        }
      },
    });
  }

  handleError(error: any) {
    if (error instanceof NotFoundError) {
      this.translate
        .get('common.errors.SERVER_SIDE.PAGE_NOT_FOUND')
        .subscribe((translations: any) => {
          this.toastMessageService.showErrorMessage(translations);
        });
    } else if (
      error.originalError &&
      error.originalError.error &&
      error.originalError.error[0]
    ) {
      let currentError = error.originalError.error[0];
      if (currentError.errorCode == 'alreadyexist') {
        if (
          currentError?.identifier &&
          currentError.identifier == 'name_pri_lang'
        )
          this.NamePrimary?.setErrors({
            duplicate: true,
          });
        else if (
          currentError?.identifier &&
          currentError.identifier == 'name_sec_lang'
        )
          this.NameSecondary?.setErrors({
            duplicate: true,
          });
        this.changeDetectorRef.detectChanges();
      }
    }
    {
      this.translate
        .get('common.errors.SERVER_SIDE.ERROR_OCCURRED')
        .subscribe((translations: any) => {
          this.toastMessageService.showErrorMessage(translations);
        });
    }
  }

  loadCategory() {
    const activeTag = this.activeTag();
    if (activeTag) {
      this.appointmentTypesService
        .getAppointmentTag(this.tenantId, activeTag?.id)
        .subscribe({
          next: (res: any) => {
            this.localActiveTag = res;
            this.buildEditForm();
            this.changeDetectorRef.detectChanges();
          },
          error: (error: any) => {
            this.handleError(error);
          },
        });
    } else {
      this.buildEditForm();
    }
  }

  get NamePrimary(): AbstractControl | null {
    return this.tagForm.get('name_primary_language');
  }
  get NameSecondary(): AbstractControl | null {
    return this.tagForm.get('name_secondary_language');
  }

  buildEditForm() {
    const activeTag = this.activeTag();
    const activeTagValue = this.activeTag();
    const activeTagVal = this.activeTag();
    this.tagForm = this.fb.group({
      name_primary_language: [
        activeTag && activeTag.name_primary_language
          ? activeTag.name_primary_language
          : '',
        Validators.required,
      ],
      name_secondary_language: [
        activeTagValue && activeTagValue.name_secondary_language
          ? activeTagValue.name_secondary_language
          : '',
      ],
      description_primary_language: [
        activeTagVal && activeTagVal.description_primary_language
          ? activeTagVal.description_primary_language
          : '',
      ],
    });
  }

  updateTag() {
    if (this.tagForm.valid) {
      let data = this.tagForm.getRawValue();
      // get value of behaviour ..
      // data.behaviour = data.behaviour.key;
      const activeTag = this.activeTag();
      if (activeTag) {
        this.appointmentTypesService
          .updateAppointmentTag(this.tenantId, activeTag.id, data)
          .subscribe({
            next: (res: any) => {
              // this.activeTag = null;
              this.changed.emit(res);
              this.translate
                .get('ADMIN.APPOINTMENT.TAG_UPDATE_MESSAGE')
                .subscribe((translations) => {
                  this.toastMessageService.showSuccess(translations);
                });
            },
            error: (error: any) => {
              this.handleError(error);
            },
          });
      } else {
        this.appointmentTypesService
          .createAppointmentTag(this.tenantId, data)
          .subscribe({
            next: (res: any) => {
              this.localActiveTag = null;
              this.changed.emit(res);
              this.translate
                .get('ADMIN.APPOINTMENT.TAG_CREATED')
                .subscribe((translations) => {
                  this.toastMessageService.showSuccess(translations);
                });
            },
            error: (error) => {
              this.handleError(error);
            },
          });
      }
    } else {
      Object.keys(this.tagForm.controls).forEach((field) => {
        const control = this.tagForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
    }
  }

  categoryCancel() {
    this.changed.emit();
  }
  closeDialog() {
    this.close.emit();
  }
}
